import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import {
  ArrowRight,
  ArrowUpRight,
  Menu,
  X,
  Instagram,
  Youtube,
  Facebook,
  Mail,
  MapPin,
  Plus,
  Play,
  ChevronLeft,
  ChevronRight,
  Check,
  AlertCircle,
  Loader2,
  Volume2,
  VolumeX,
} from "lucide-react";
import RouteMap, { ITINERARY } from "@/components/RouteMap";
import YouTubeLive from "@/components/YouTubeLive";
import YouTubeShorts from "@/components/YouTubeShorts";
import MediaHub from "@/components/MediaHub";
import InstagramFollow from "@/components/InstagramFollow";
import Countdown from "@/components/Countdown";
import Community from "@/components/Community";

const WHATSAPP_NUMBER = "19733915664"; // owner — Path B wa.me link

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROPOSAL_URL =
  "https://customer-assets.emergentagent.com/wingman/f924726d-57b3-401a-a4cc-de17ccf5ab21/attachments/c9271a0eecb842c7bdb3de9575ef9560_index.html";

const IMG_SHAMAN = "/team/odko.jpg?v=1";
const IMG_MOGI = "/team/mogi.jpg?v=1";
const IMG_MUUGII = "/team/muugii.jpg?v=2";
const LOGO_URL = "/logo.png?v=11";
const IMG_HERO =
  "https://images.unsplash.com/photo-1504851149312-7a075b496cc7?auto=format&fit=crop&w=2400&q=80"; // milky way over desert
const IMG_CYBER =
  "https://images.unsplash.com/photo-1701204574994-d27e51f63eb1?auto=format&fit=crop&w=2000&q=80"; // cybertruck

/* ========== Reveal-on-scroll hook ========== */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ========== Navigation ========== */
const NavLinks = [
  { id: "live", label: "Latest" },
  { id: "community", label: "Community" },
  { id: "vision", label: "Vision" },
  { id: "route", label: "The Route" },
  { id: "team", label: "Team" },
  { id: "contact", label: "Contact" },
];

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 py-5">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-2 group"
        >
          <img
            src={LOGO_URL}
            alt="Spiritual Road Trip"
            className="h-9 w-9 md:h-10 md:w-10 object-contain"
          />
          <span className="font-serif-display text-base md:text-lg tracking-wide hidden sm:block">
            Spiritual Road Trip
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NavLinks.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-${l.id}`}
              className="text-xs uppercase tracking-overline text-zinc-400 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3" />

        <button
          aria-label="Open menu"
          data-testid="nav-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen((s) => !s)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-black/95 border-t border-white/10 px-6 py-8 flex flex-col gap-6"
        >
          {NavLinks.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`mobile-nav-${l.id}`}
              onClick={() => setOpen(false)}
              className="text-sm uppercase tracking-overline text-zinc-300"
            >
              {l.label}
            </a>
          ))}
        </div>
      )}
    </header>
  );
};

/* ========== Hero ========== */
const HERO_VIDEO_URL =
  "https://customer-assets.emergentagent.com/job_sixth-sense-trip/artifacts/mxv4h70b_Intro%2012%20sec%20social%20media.mov";

const Hero = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    setMuted(next);
    if (!next) v.play().catch(() => {});
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen h-screen flex items-end overflow-hidden grain"
    >
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          src={HERO_VIDEO_URL}
          poster={IMG_HERO}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="hero-video"
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/55 to-black" />
      </div>

      {/* Mute / Unmute toggle */}
      <button
        type="button"
        onClick={toggleMute}
        data-testid="hero-mute-toggle"
        aria-label={muted ? "Unmute video" : "Mute video"}
        className="absolute top-24 right-6 md:right-12 z-20 inline-flex items-center gap-2 px-4 py-2 border border-gold/60 bg-black/50 backdrop-blur-md text-gold hover:bg-gold hover:text-black transition-colors text-[11px] uppercase tracking-overline"
      >
        {muted ? (
          <>
            <VolumeX className="w-4 h-4" />
            <span className="hidden sm:inline">Sound Off</span>
          </>
        ) : (
          <>
            <Volume2 className="w-4 h-4" />
            <span className="hidden sm:inline">Sound On</span>
          </>
        )}
      </button>

      <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-12 pb-16 md:pb-24 pt-36">
        <div className="flex items-center gap-3 mb-8 reveal">
          <span className="w-10 h-px bg-gold" />
          <span className="text-xs uppercase tracking-overline text-zinc-300">
            June & July 2026 · A 36-Day Spiritual Expedition
          </span>
        </div>

        <h1
          data-testid="hero-title"
          className="font-serif-display text-[clamp(2.75rem,9vw,8.5rem)] leading-[0.95] tracking-tight max-w-5xl reveal"
        >
          The <em className="italic text-gold">Spiritual</em>
          <br />
          Road Trip.
        </h1>

        <p className="mt-8 max-w-2xl text-lg md:text-xl text-zinc-300 leading-[1.65] font-light reveal">
          A cross-country expedition exploring spirituality, consciousness, and
          human connection through the lens of modern innovation — our shared
          journey, delivered straight to you on{" "}
          <span className="text-white">YouTube</span>.
        </p>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6 max-w-4xl border-t border-white/10 pt-8 reveal">
          {[
            ["36", "Days"],
            ["11,000+", "Miles Filmed"],
            ["21", "States + DC"],
            ["17", "National, State Parks + Monuments"],
          ].map(([n, l]) => (
            <div key={l}>
              <div className="font-serif-display text-3xl md:text-4xl text-white">
                {n}
              </div>
              <div className="text-[11px] uppercase tracking-overline text-zinc-500 mt-2">
                {l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ========== Vision / About ========== */
const Vision = () => (
  <section
    id="vision"
    data-testid="vision-section"
    className="relative py-20 md:py-28 px-6 md:px-12 bg-[#050505]"
  >
    <div className="max-w-3xl mx-auto">
      {/* Section overline */}
      <div className="flex items-center gap-3 mb-10 reveal">
        <span className="w-8 h-px bg-gold" />
        <span className="text-xs uppercase tracking-overline text-zinc-500">
          02 — Our Mission
        </span>
      </div>

      {/* H1 — page-level mission title */}
      <h1
        className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight text-white reveal"
        data-testid="mission-h1"
      >
        Our Mission:{" "}
        <em className="italic text-gold">
          Seeking Connection, Healing, and Spiritual Understanding
        </em>
      </h1>

      {/* Body — single legible font (serif-display kept throughout for cohesion) */}
      <div
        className="mt-12 space-y-7 text-zinc-200 text-lg md:text-xl font-light leading-[1.75] tracking-[0.005em] reveal"
        data-testid="mission-body"
      >
        <p>
          <span className="text-white">The Spiritual Road Trip</span> is an{" "}
          <span className="text-white">11,000+ miles filmed</span> dedicated to
          exploring the diverse spiritual paths that define the human
          experience. Our mission is to provide a raw glimpse into the many
          ways people express faith, worship, and practice their personal
          beliefs.
        </p>

        {/* Cybertruck subsection */}
        <div className="pt-6 pb-2">
          <p>
            In this trip, we will be utilizing the modern marvel of
            technology — the{" "}
            <span className="text-white">Tesla Cybertruck</span> — to carry
            our physical bodies to sacred destinations while we explore the
            spirit world and the depths of the unseen.
          </p>
        </div>

        {/* H2 — the deeper exploration */}
        <div className="pt-6 pb-2">
          <h2
            className="font-serif-display text-2xl md:text-4xl leading-tight tracking-tight text-white mb-6"
            data-testid="mission-storytelling-h2"
          >
            Authentic Storytelling, Honest Questions
          </h2>
          <p>
            Through <span className="text-white">authentic storytelling</span>,
            we explore the deep questions of God, existence, and our shared{" "}
            <span className="text-white">purpose in life</span>. By connecting
            with healers, mystics, and elders — including a traditional{" "}
            <span className="text-white">Mongolian Shaman</span> — we document
            how <span className="text-white">spirituality</span> leads to
            personal <span className="text-white">healing</span> and a
            profound <span className="text-white">connection to nature</span>{" "}
            and our higher soul.
          </p>
        </div>

        {/* Closing — emphasized as a quiet pull-quote */}
        <p
          className="pt-6 font-serif-display italic text-2xl md:text-3xl text-white leading-snug"
          data-testid="mission-closing"
        >
          We hope this content serves as a source of hope and a clear
          direction for those searching for their own path to{" "}
          <span className="text-gold">higher consciousness</span>.
        </p>
      </div>

      {/* Hidden SEO description for search & AI crawlers */}
      <p className="sr-only">
        The Spiritual Road Trip is a documentary expedition exploring
        spirituality, Mongolian Shamanism, Tesla Cybertruck Full Self-Driving
        technology, healing, connection to nature, and purpose in life across
        11,000+ miles filmed through 21 states and 17 National Parks, State Parks, and monuments.
      </p>
    </div>
  </section>
);

/* ========== Route ========== */
const ROUTE_STATS = [
  { n: "36", l: "Days Total", sub: "June & July 2026" },
  { n: "11,000+", l: "Miles Filmed", sub: "Coast to coast and back" },
  { n: "21", l: "States + D.C.", sub: "One continental loop" },
  { n: "17", l: "National, State Parks + Monuments", sub: "Iconic destinations" },
];

const PARKS = [
  "Badlands",
  "Yellowstone",
  "Yosemite",
  "Sequoia",
  "Zion",
  "Great Sand Dunes",
  "Mesa Verde",
];

const RouteSection = () => (
  <section
    id="route"
    data-testid="route-section"
    className="relative py-20 md:py-28 overflow-hidden"
  >
    <div className="absolute inset-0">
      <img
        src={IMG_CYBER}
        alt="Cybertruck"
        className="w-full h-full object-cover opacity-20"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black" />
    </div>

    <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
      <div className="mb-12 reveal">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-gold" />
          <span className="text-xs uppercase tracking-overline text-zinc-500">
            03 — The Route
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 border-t border-white/10 reveal">
        {ROUTE_STATS.map((s, i) => (
          <div
            key={s.l}
            className={`p-8 md:p-10 border-b border-white/10 ${
              i % 4 !== 3 ? "md:border-r" : ""
            } ${
              i % 2 !== 1 ? "border-r" : ""
            } md:border-r-white/10 hover:bg-white/5 transition-colors duration-500 group`}
            data-testid={`route-stat-${i}`}
          >
            <div className="font-serif-display text-5xl md:text-6xl tracking-tight text-white group-hover:text-gold transition-colors">
              {s.n}
            </div>
            <div className="mt-4 text-xs uppercase tracking-overline text-zinc-300">
              {s.l}
            </div>
            <div className="mt-2 text-sm text-zinc-500 font-light">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Interactive Map */}
      <div className="mt-12 reveal" data-testid="route-map-wrapper">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-overline text-zinc-500 mb-2">
              The Map
            </div>
            <h3 className="font-serif-display text-2xl md:text-3xl tracking-tight">
              36 stops · 36-day arc · weather & lore at every pin
            </h3>
          </div>
          <div className="hidden md:block text-xs uppercase tracking-overline text-zinc-500">
            Tap a marker to explore
          </div>
        </div>
        <RouteMap />
      </div>

      {/* Route at a Glance — tight vertical list */}
      <div
        className="mt-16 md:mt-24 reveal"
        data-testid="route-at-a-glance"
      >
        <div className="mb-8 flex items-center gap-3">
          <span className="w-8 h-px bg-gold" />
          <span className="text-xs uppercase tracking-overline text-zinc-500">
            The Route at a Glance
          </span>
        </div>

        <ol
          className="grid grid-cols-1 md:grid-cols-2 gap-x-12 border-t border-white/10"
          data-testid="route-list"
        >
          {ITINERARY.map((stop, i) => (
            <li
              key={`${stop.name}-${i}`}
              data-testid={`route-list-${i}`}
              className="group flex items-baseline gap-4 py-2.5 border-b border-white/5 hover:bg-white/[0.025] transition-colors duration-300"
            >
              <span className="text-[10px] uppercase tracking-overline text-zinc-600 w-7 shrink-0 tabular-nums group-hover:text-gold transition-colors">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-zinc-200 font-light text-[18px] flex-1 group-hover:text-white transition-colors">
                {stop.name}
              </span>
              <span className="text-[10px] uppercase tracking-overline text-zinc-500 shrink-0">
                {stop.state}
              </span>
            </li>
          ))}
        </ol>

        <p
          className="mt-8 max-w-3xl text-sm md:text-[15px] font-light italic text-zinc-500 leading-relaxed"
          data-testid="route-flexibility-note"
        >
          <span className="not-italic text-[10px] uppercase tracking-overline text-gold mr-3 align-middle">
            A Living Itinerary
          </span>
          This route is a guide, not a contract. Additional national parks,
          spiritual sites, and personalities may be added — or quietly set
          aside — as the road, the weather, and the conversations unfold. The
          spirit of the journey is to remain open to the unexpected.
        </p>
      </div>

      {/* Marquee parks */}
      <div className="mt-12 overflow-hidden border-y border-white/10 py-6 reveal">
        <div className="flex marquee-track gap-12 whitespace-nowrap">
          {[...PARKS, ...PARKS].map((p, i) => (
            <span
              key={`${p}-${i}`}
              className="font-serif-display text-2xl md:text-3xl text-zinc-300 flex items-center gap-12"
            >
              <MapPin className="w-4 h-4 text-gold" /> {p}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ========== Team ========== */
const Team = () => (
  <section
    id="team"
    data-testid="team-section"
    className="py-20 md:py-28 px-6 md:px-12"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3 mb-6 reveal">
        <span className="w-8 h-px bg-gold" />
        <span className="text-xs uppercase tracking-overline text-zinc-500">
          04 — Meet the Team
        </span>
      </div>
      <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-4xl mb-8 reveal">
        One former champion wrestler.{" "}
        <em className="italic text-gold">One Mongolian shaman.</em>
      </h2>
      <p className="max-w-3xl text-lg md:text-xl text-zinc-400 font-light leading-[1.65] mb-16 reveal">
        9,000 miles across America in a Cybertruck. This is the team behind
        Spiritual Road Trip.
      </p>

      <article
        data-testid="team-card"
        className="group relative overflow-hidden bg-[#0d0d0d] border border-white/10 reveal"
      >
        {[
          {
            name: "Mogi (Munkhbaatar Baatar)",
            role: "Creator & Host",
            img: IMG_MOGI,
            tag: "The Investigator & Modern Nomad",
            bio: "Mogi has spent his life crossing borders — athletic, cultural, and spiritual. A 20-year competitor, he went from Mongolia's junior national freestyle wrestling team to NCAA Division III All-American and IBJJF World Masters Jiu-Jitsu Champion. He holds a Physics degree, graduated from the IOC's elite ICECP program, and built Mongolia's first Airbnb experience — a horse-riding tour rated 4.9/5. At 33, he's explored 21+ countries, coaches wrestling, and is a father of four. On this journey, he's the bridge between future tech and ancient wisdom.",
          },
          {
            name: "Odko (Odgerel Gerelchuluun)",
            role: "Mongolian Shaman",
            img: IMG_SHAMAN,
            tag: "The Voice of the Etheric",
            bio: "Odgerel has practiced Mongolian shamanism for 27 years. Since 1999, she has held quarterly rituals without interruption, keeping the connection alive between the Eternal Blue Sky and the Mongolian heartland. Her specialty is the rarest work in the tradition: the elevation and liberation of souls — roughly 32,000 guided to date. She is the only shaman in Mongolia to have built nine sacred shrines across the country, and her work has been featured on Thai National TV. She joins the trip to connect the traditions of the steppes with the sacred landscapes of the American West.",
          },
          {
            name: "Muugii",
            role: "Volunteer Driver",
            img: IMG_MUUGII,
            tag: "Behind the Wheel",
            bio: "Travel enthusiast and the man behind the wheel for the first leg of the journey.",
          },
        ].map((p, i) => (
          <div
            key={p.name}
            data-testid={`team-member-${i}`}
            className={`grid grid-cols-1 md:grid-cols-[minmax(240px,320px)_1fr] gap-6 md:gap-10 p-6 md:p-10 ${
              i > 0 ? "border-t border-white/10" : ""
            }`}
          >
            {/* Portrait */}
            <div className="relative w-full aspect-[4/5] overflow-hidden bg-[#0a0a0a]">
              {p.img ? (
                <img
                  src={p.img}
                  alt={p.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-1000 group-hover:scale-105"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] via-[#0d0d0d] to-[#0a0a0a]">
                  <span className="font-serif-display text-6xl md:text-7xl text-gold/40">
                    {p.name.charAt(0)}
                  </span>
                </div>
              )}
              <div className="absolute top-4 left-4 text-[10px] uppercase tracking-overline text-zinc-200 bg-black/50 backdrop-blur-sm px-3 py-1.5">
                {p.tag}
              </div>
            </div>

            {/* Text content */}
            <div className="flex flex-col justify-center">
              <div className="font-serif-display text-3xl md:text-4xl text-white leading-tight">
                {p.name}
              </div>
              <div className="text-sm text-gold mt-2 uppercase tracking-overline">
                {p.role}
              </div>
              <p className="mt-5 text-zinc-400 font-light leading-[1.7] text-[17px] md:text-[18px]">
                {p.bio}
              </p>
            </div>
          </div>
        ))}
      </article>
    </div>
  </section>
);


/* ========== Contact Form ========== */
// Strict-enough email regex: requires "x@y.tld" with at least 2-char TLD
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const ContactSection = ({ tier, setTier }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    message: "",
    website: "", // honeypot — humans never fill this
  });
  const [emailError, setEmailError] = useState("");
  const [humanVerified, setHumanVerified] = useState(false);
  // status: "idle" | "sending" | "sent" | "error"
  const [status, setStatus] = useState("idle");
  const [submitted, setSubmitted] = useState(null);

  const update = (k) => (e) => {
    const value = e.target.value;
    setForm((s) => ({ ...s, [k]: value }));
    if (k === "email") {
      if (!value) setEmailError("");
      else if (!EMAIL_REGEX.test(value.trim()))
        setEmailError("Please enter a valid email like name@example.com");
      else setEmailError("");
    }
  };

  const emailValid = EMAIL_REGEX.test(form.email.trim());
  const canSubmit =
    humanVerified &&
    emailValid &&
    !!form.name.trim() &&
    !!form.message.trim() &&
    !!tier &&
    status !== "sending";

  const submit = async (e) => {
    e.preventDefault();
    if (form.website) return; // bot tripped the honeypot — silently drop
    if (!humanVerified) {
      toast.error("Please confirm you're human before sending.");
      return;
    }
    if (!emailValid) {
      setEmailError("Please enter a valid email like name@example.com");
      return;
    }
    if (!tier) {
      toast.error("Please select an interest area.");
      return;
    }
    setStatus("sending");
    try {
      const payload = {
        name: form.name,
        email: form.email,
        organization: form.organization || null,
        interest_tier: tier,
        message: form.message,
      };
      await axios.post(`${API}/inquiries`, payload);
      setSubmitted({ ...payload });
      setStatus("sent");
      // Reset form after the success state has animated in
      setForm({ name: "", email: "", organization: "", message: "", website: "" });
      setTier("");
      setHumanVerified(false);
      setEmailError("");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
      setStatus("error");
      // After a moment, return to idle so they can retry
      setTimeout(() => setStatus("idle"), 2500);
    }
  };

  const whatsappHref = submitted
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        `Hi Mogi — I just submitted an inquiry through your site.\n\nName: ${submitted.name}\nOrganization: ${submitted.organization || "—"}\nInterest: ${submitted.interest_tier}\n\n${submitted.message}`
      )}`
    : "";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-28 px-6 md:px-12 bg-[#070707]"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-12">
        <div className="col-span-12 md:col-span-5 reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-gold" />
            <span className="text-xs uppercase tracking-overline text-zinc-500">
              05 — Contact
            </span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02] tracking-tight">
            Let’s create something{" "}
            <em className="italic text-gold">extraordinary</em> together.
          </h2>
          <p className="mt-8 text-zinc-400 font-light leading-[1.7] text-lg max-w-md">
            Currently seeking strategic sponsors, media partners, production
            collaborators, and visionary brands who want to be part of a
            groundbreaking storytelling experience.
          </p>
        </div>

        <form
          onSubmit={submit}
          data-testid="inquiry-form"
          noValidate
          className="col-span-12 md:col-span-6 md:col-start-7 space-y-6 reveal"
        >
          {/* Honeypot — visually hidden, accessibility-hidden, off-screen.
              Real humans never fill this. Bots that auto-fill every field will. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          >
            <label htmlFor="website-hp">Website (leave blank)</label>
            <input
              id="website-hp"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={update("website")}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label
                htmlFor="name"
                className="text-xs uppercase tracking-overline text-zinc-400"
              >
                Name *
              </Label>
              <Input
                id="name"
                data-testid="form-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Your full name"
                className="bg-transparent border-x-0 border-t-0 border-b border-white/20 rounded-none px-0 h-12 focus-visible:ring-0 focus-visible:border-gold text-white placeholder:text-zinc-600"
                required
              />
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-xs uppercase tracking-overline text-zinc-400"
              >
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="form-email"
                value={form.email}
                onChange={update("email")}
                onBlur={() => {
                  if (form.email && !emailValid)
                    setEmailError("Please enter a valid email like name@example.com");
                }}
                aria-invalid={!!emailError}
                aria-describedby={emailError ? "email-error" : undefined}
                placeholder="you@brand.com"
                className={`bg-transparent border-x-0 border-t-0 border-b rounded-none px-0 h-12 focus-visible:ring-0 text-white placeholder:text-zinc-600 transition-colors ${
                  emailError
                    ? "border-red-500/70 focus-visible:border-red-500"
                    : "border-white/20 focus-visible:border-gold"
                }`}
                required
              />
              {emailError && (
                <div
                  id="email-error"
                  data-testid="form-email-error"
                  className="flex items-center gap-2 text-xs text-red-400 mt-1.5"
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                  <span>{emailError}</span>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="organization"
              className="text-xs uppercase tracking-overline text-zinc-400"
            >
              Organization
            </Label>
            <Input
              id="organization"
              data-testid="form-organization"
              value={form.organization}
              onChange={update("organization")}
              placeholder="Brand, studio, or company (optional)"
              className="bg-transparent border-x-0 border-t-0 border-b border-white/20 rounded-none px-0 h-12 focus-visible:ring-0 focus-visible:border-gold text-white placeholder:text-zinc-600"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-overline text-zinc-400">
              Interest *
            </Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger
                data-testid="form-tier"
                className="bg-transparent border-x-0 border-t-0 border-b border-white/20 rounded-none px-0 h-12 focus:ring-0 text-white"
              >
                <SelectValue placeholder="Select an area of interest" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-white/10 text-white rounded-none">
                <SelectItem value="Sponsorship Inquiry">Sponsorship Inquiry</SelectItem>
                <SelectItem value="Title Sponsor">Title Sponsor</SelectItem>
                <SelectItem value="Route Partner">Route Partner</SelectItem>
                <SelectItem value="Media Sponsor">Media Sponsor</SelectItem>
                <SelectItem value="Creative Collaboration">
                  Creative Collaboration
                </SelectItem>
                <SelectItem value="Distribution / Press">
                  Distribution / Press
                </SelectItem>
                <SelectItem value="General Inquiry">General Inquiry</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="message"
              className="text-xs uppercase tracking-overline text-zinc-400"
            >
              Message *
            </Label>
            <Textarea
              id="message"
              data-testid="form-message"
              value={form.message}
              onChange={update("message")}
              placeholder="Tell us about your brand, vision, or how you'd like to collaborate..."
              rows={5}
              className="bg-transparent border-x-0 border-t-0 border-b border-white/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-gold text-white placeholder:text-zinc-600 resize-none"
              required
            />
          </div>

          {/* Human verification — minimalist, custom-styled */}
          <button
            type="button"
            role="checkbox"
            aria-checked={humanVerified}
            data-testid="human-check"
            onClick={() => setHumanVerified((v) => !v)}
            className={`w-full flex items-center gap-4 p-4 border transition-all duration-300 text-left group ${
              humanVerified
                ? "border-gold/60 bg-gold/[0.04]"
                : "border-white/15 hover:border-white/30 bg-transparent"
            }`}
          >
            <span
              className={`relative shrink-0 w-6 h-6 border flex items-center justify-center transition-all duration-300 ${
                humanVerified
                  ? "border-gold bg-gold"
                  : "border-white/25 group-hover:border-white/50"
              }`}
            >
              <Check
                className={`w-4 h-4 text-black transition-all duration-300 ${
                  humanVerified ? "opacity-100 scale-100" : "opacity-0 scale-50"
                }`}
                strokeWidth={3}
              />
            </span>
            <span className="flex-1 min-w-0">
              <span
                className={`block text-sm font-medium transition-colors ${
                  humanVerified ? "text-gold" : "text-zinc-200"
                }`}
              >
                {humanVerified ? "Verified — you're human" : "I'm human"}
              </span>
              <span className="block text-[11px] uppercase tracking-overline text-zinc-500 mt-1">
                {humanVerified
                  ? "Thanks. You may send your inquiry."
                  : "Tap to confirm before sending."}
              </span>
            </span>
          </button>

          {/* Submit button — three visual states */}
          <Button
            type="submit"
            disabled={!canSubmit}
            data-testid="form-submit"
            className={`w-full h-14 rounded-none uppercase tracking-overline text-xs transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed ${
              status === "sent"
                ? "bg-green-500 hover:bg-green-500 text-black"
                : "bg-gold text-black hover:bg-white"
            }`}
          >
            <span className="inline-flex items-center justify-center gap-2">
              {status === "sending" && (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                  Sending...
                </>
              )}
              {status === "sent" && (
                <>
                  <Check className="w-4 h-4" strokeWidth={3} />
                  Message Sent
                </>
              )}
              {(status === "idle" || status === "error") && (
                <>
                  Send Inquiry
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </span>
          </Button>

          {submitted && status === "sent" && (
            <div
              data-testid="form-success-block"
              className="mt-6 border border-gold/30 bg-[#0e0d09] p-6 animate-in fade-in slide-in-from-bottom-2 duration-500"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-2 h-2 bg-gold rounded-full pulse-dot" />
                <span className="text-xs uppercase tracking-overline text-gold">
                  Inquiry received
                </span>
              </div>
              <p className="text-zinc-300 font-light text-sm leading-relaxed">
                Thanks, {submitted.name.split(" ")[0]}. We’ll respond by email
                within a few days. Want a faster reply? Ping Mogi directly on
                WhatsApp — your inquiry will be pre-filled.
              </p>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-followup-btn"
                className="mt-5 inline-flex items-center gap-3 px-5 py-3 bg-[#25d366] text-black text-xs uppercase tracking-overline font-medium hover:brightness-110 transition"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                  <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
                </svg>
                Notify Mogi on WhatsApp
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          )}
        </form>
      </div>
    </section>
  );
};

/* ========== Footer ========== */
const Footer = () => (
  <footer
    data-testid="site-footer"
    className="relative pt-20 md:pt-28 pb-10 px-6 md:px-12 border-t border-white/10"
  >
    <div className="max-w-[1400px] mx-auto">
      <a
        href="#contact"
        data-testid="footer-cta"
        className="block font-serif-display tracking-tighter leading-[0.85] text-[18vw] md:text-[14vw] hover:text-gold transition-colors duration-700"
      >
        Let’s<em className="italic">.</em>Talk
      </a>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10 border-t border-white/10 pt-10">
        <div>
          <div className="text-xs uppercase tracking-overline text-zinc-500 mb-3">
            Project
          </div>
          <div className="font-serif-display text-xl">
            The Spiritual Road Trip
          </div>
          <div className="text-sm text-zinc-500 mt-2">
            Summer 2026 · Spiritual Cross-Country Expedition
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-overline text-zinc-500 mb-3">
            Connect
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <a
              href="https://www.instagram.com/sixthsenseroadtrip/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="text-zinc-300 hover:text-gold transition-colors flex items-center gap-2"
            >
              <Instagram className="w-4 h-4" /> Instagram
            </a>
            <a
              href="https://x.com/Mogi_B"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-x"
              className="text-zinc-300 hover:text-gold transition-colors flex items-center gap-2"
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-4 h-4"
                aria-hidden="true"
              >
                <path d="M18.244 2H21l-6.52 7.45L22 22h-6.6l-5.16-6.74L4.4 22H1.64l6.97-7.96L1.5 2h6.77l4.66 6.16L18.244 2Zm-1.16 18h1.79L7.02 4h-1.9l11.964 16Z" />
              </svg>{" "}
              X (Twitter)
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61590438424463"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              className="text-zinc-300 hover:text-gold transition-colors flex items-center gap-2"
            >
              <Facebook className="w-4 h-4" /> Facebook
            </a>
            <a
              href="https://www.tiktok.com/@sixthsenseroadtrip"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-tiktok"
              className="text-zinc-300 hover:text-gold transition-colors flex items-center gap-2"
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-4 h-4"
                aria-hidden="true"
              >
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.91a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31Z" />
              </svg>{" "}
              TikTok
            </a>
            <a
              href="https://www.youtube.com/channel/UCxvmL3w8S2e6tTPN18sE5Fg"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-youtube"
              className="text-zinc-300 hover:text-gold transition-colors flex items-center gap-2"
            >
              <Youtube className="w-4 h-4" /> YouTube
            </a>
          </div>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between gap-4 text-xs text-zinc-600 uppercase tracking-overline">
        <div>© {new Date().getFullYear()} The Spiritual Road Trip</div>
        <div>Ancient Wisdom · Through a Lens</div>
      </div>
    </div>
  </footer>
);

/* ========== Page ========== */
const Home = () => {
  const [tier, setTier] = useState("");
  useReveal();

  // Scroll-based subtle navigation highlight effect (placeholder hook for future)
  const ref = useRef(null);

  return (
    <div ref={ref} className="bg-[#050505] text-white">
      <Navigation />
      <main>
        <Hero />
        <MediaHub />
        <InstagramFollow />
        <Community />
        <Countdown />
        <Vision />
        <RouteSection />
        <Team />
        <ContactSection tier={tier} setTier={setTier} />
      </main>
      <Footer />
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0a0a0a",
            border: "1px solid rgba(212,175,55,0.3)",
            color: "#fff",
            borderRadius: 0,
          },
        }}
      />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
